import React, { ReactNode, useCallback } from "react";

import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { useIntl } from "react-intl";

import { stopEvents } from "@mapmycustomers/shared/util/browser";
import { Modal, ModalProps, Popconfirm, PopconfirmProps } from "@mapmycustomers/ui";

import styles from "./EntityModal.module.scss";

interface Props extends ModalProps {
  confirmationText?: ReactNode;
  entityTypeName?: string;
  showConfirm?: boolean;
}

const EntityModal = ({
  children,
  className,
  confirmationText,
  entityTypeName,
  onCancel,
  showConfirm,
  ...props
}: Props) => {
  const intl = useIntl();
  const confirmationMessage =
    confirmationText ??
    intl.formatMessage(
      {
        id: "createEditEntity.baseModal.close.popconfirm.title",
        defaultMessage: "Are you sure you want to {br}discard this record without {br}saving?",
        description: "Confirmation text when closing Base Modal",
      },
      {
        br: <br />,
      }
    );

  const handlePopupCancel = useCallback<NonNullable<PopconfirmProps["onCancel"]>>((e) => {
    stopEvents(e);
  }, []);

  const handlePopupConfirm = useCallback<NonNullable<PopconfirmProps["onConfirm"]>>(
    (e) => {
      if (e) {
        onCancel?.(e);
      }
    },
    [onCancel]
  );

  return (
    <Modal
      cancelButtonProps={{ hidden: true }}
      centered
      className={cn(styles.container, className)}
      closeIcon={
        <p onClick={showConfirm ? stopEvents : undefined}>
          <Popconfirm
            cancelText={intl.formatMessage({
              id: "createEditEntity.baseModal.close.popconfirm.cancel",
              defaultMessage: "Cancel",
              description: "Cancel button for close modal popconfirm",
            })}
            description={confirmationMessage}
            disabled={!showConfirm}
            okText={intl.formatMessage({
              id: "createEditEntity.baseModal.close.popconfirm.discard",
              defaultMessage: "Discard",
              description: "Discard button for close modal popconfirm",
            })}
            onCancel={handlePopupCancel}
            onConfirm={handlePopupConfirm}
            placement="rightTop"
          >
            <FontAwesomeIcon icon={faTimes} />
          </Popconfirm>
        </p>
      }
      maskClosable={false}
      onCancel={onCancel}
      open
      scrollable
      width="clamp(100vw, 100vw, 750px)"
      {...props}
    >
      {children}
    </Modal>
  );
};

export default EntityModal;
